import { Button, List, ListItem, ListItemText, Paper, Typography } from '@mui/material'
import get from 'lodash/get'
import React from 'react'
import { Mutation, Query } from 'react-apollo'
import DeleteButton from '../../common/iconButtons/DeleteButton'
import EditButton from '../../common/iconButtons/EditButton'
import Loader from '../../common/loader/Loader'
import Textarea from '../../common/textarea/Textarea'
import { i18n } from '../index'
import pageCss from './monthlyInfoPage.css'
import { createNote, deleteNote, fetchMonthlyInfoNotes } from './monthlyInfoQueries'
import type { MonthlyInfoNotes, NoteSectionType, NotesListVariables } from './monthlyInfoTypes'
import NoteDialog from './NoteDialog'
import css from './noteSection.css'

type NoteSectionProps = {
  stationId: string
  date: string
  section: NoteSectionType
}

type NoteSectionState = {
  newNote: string
  isDialogOpen: boolean
  selectedNote: any
}

function updateDeleteNote(
  noteId: string,
  date: string,
  stationId: string,
  section: NoteSectionType,
): (...args: Array<any>) => any {
  return (cache, { data: { deleteMonthlyInfoNotes } }) => {
    if (deleteMonthlyInfoNotes) {
      const data = cache.readQuery({
        query: fetchMonthlyInfoNotes,
        variables: {
          date,
          stationId,
          type: section,
        },
      })
      data.monthlyInfoNotes = get(data, 'monthlyInfoNotes', []).filter((note) => note.id !== noteId)
      cache.writeQuery({
        query: fetchMonthlyInfoNotes,
        data,
        variables: {
          date,
          stationId,
          type: section,
        },
      })
    }
  }
}

function updateCreateNote(date: string, stationId: string, section: NoteSectionType): (...args: Array<any>) => any {
  return (cache, { data: { createMonthlyInfoNotes: addedNote } }) => {
    const data = cache.readQuery({
      query: fetchMonthlyInfoNotes,
      variables: {
        date,
        stationId,
        type: section,
      },
    })
    data.monthlyInfoNotes.push(addedNote)
    cache.writeQuery({
      query: fetchMonthlyInfoNotes,
      data,
      variables: {
        date,
        stationId,
        type: section,
      },
    })
  }
}

class NoteSection extends React.Component<NoteSectionProps, NoteSectionState> {
  state = {
    newNote: '',
    isDialogOpen: false,
    selectedNote: null,
  }

  openDialog = () => {
    this.setState({
      isDialogOpen: true,
    })
  }

  closeDialog = () => {
    this.setState({
      isDialogOpen: false,
      selectedNote: null,
    })
  }

  onEdit = (selectedNote: any) => {
    this.setState({
      selectedNote,
    })
    this.openDialog()
  }

  onChangeNewNoteText = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    this.setState({
      newNote: e.target.value,
    })
  }

  addNewNote = (onMutate: (...args: Array<any>) => any) => () => {
    const { date, stationId, section } = this.props
    const { newNote } = this.state
    onMutate({
      variables: {
        date,
        stationId,
        type: section,
        body: newNote,
      },
    })
    this.setState({
      newNote: '',
    })
  }

  render() {
    const { date, stationId, section } = this.props
    const { selectedNote, isDialogOpen, newNote } = this.state
    return (
      <div className={pageCss.noteSection}>
        <Typography variant='h6' gutterBottom>
          {i18n.t('monthlyInfo.note.sectionTitle')}
        </Typography>
        {/* form to add new note */}
        <Mutation mutation={createNote} update={updateCreateNote(date, stationId, section)}>
          {(onMutate) => (
            <div className={css.newNote}>
              <Textarea
                className={css.textarea}
                label={i18n.t('monthlyInfo.note.newNote')}
                value={newNote}
                onChange={this.onChangeNewNoteText}
              />
              <Button
                color='primary'
                variant='contained'
                onClick={this.addNewNote(onMutate)}
                className={css.button}
                disabled={!newNote}
              >
                {i18n.t('monthlyInfo.note.addButton')}
              </Button>
            </div>
          )}
        </Mutation>
        {/* list of notes */}
        <Query<
          {
            monthlyInfo: Array<MonthlyInfoNotes>
          },
          NotesListVariables
        >
          query={fetchMonthlyInfoNotes}
          variables={{
            date,
            stationId,
            type: section,
          }}
        >
          {({ data, error, loading }) => {
            const notes = get(data, 'monthlyInfoNotes', [])
            return (
              <Loader
                {...{
                  error,
                  loading,
                }}
              >
                <NoteDialog note={selectedNote} isOpen={isDialogOpen} onClose={this.closeDialog} />
                <Paper>
                  <List className={css.notesList}>
                    {notes.map((note, index) => (
                      <ListItem key={note.id} divider={index !== notes.length - 1}>
                        <ListItemText className={css.noteText}>{note.body}</ListItemText>
                        <EditButton className={css.itemButton} onClick={() => this.onEdit(note)} />
                        <Mutation mutation={deleteNote} update={updateDeleteNote(note.id, date, stationId, section)}>
                          {(onMutate) => (
                            <DeleteButton
                              className={css.itemButton}
                              onClick={() =>
                                onMutate({
                                  variables: {
                                    noteId: note.id,
                                  },
                                })
                              }
                            />
                          )}
                        </Mutation>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Loader>
            )
          }}
        </Query>
      </div>
    )
  }
}

export default NoteSection
